// ============================================
// Meet the Doctor — Dr. Christopher Calapai, D.O.
//
// Route: /doctor
// Design system: light theme, Instrument Serif titles,
// DM Sans body, Rose accent (#E11D48), WCAG AA.
// No framer-motion — pure CSS transitions only.
// ============================================

import { useState, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Design tokens ───

const C = {
  bg: '#FAFAFA',
  bgWhite: '#FFFFFF',
  bgSoft: '#F8FAFC',
  accent: '#E11D48',
  accentSoft: 'rgba(225,29,72,0.06)',
  accentGlow: 'rgba(225,29,72,0.15)',
  navy: '#0F172A',
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  border: '#E4E4E7',
  borderLight: '#F4F4F5',
  btn: '#E11D48',
  btnHover: '#BE123C',
};

const FONT_SERIF = "'Instrument Serif', Georgia, serif";
const FONT_BODY = "'DM Sans', -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace";

// ─── FadeIn on scroll (pure CSS, no framer-motion) ───

function FadeIn({
  children,
  delay = 0,
  direction = 'up',
  style: extra = {},
}: {
  children: ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.12 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const transforms: Record<string, string> = {
    up: 'translateY(24px)',
    down: 'translateY(-24px)',
    left: 'translateX(24px)',
    right: 'translateX(-24px)',
    none: 'none',
  };

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : transforms[direction],
        transition: `opacity .6s cubic-bezier(.16,1,.3,1) ${delay}s, transform .6s cubic-bezier(.16,1,.3,1) ${delay}s`,
        ...extra,
      }}
    >
      {children}
    </div>
  );
}

// ─── Credentials data ───

const credentials = [
  'Doctor of Osteopathic Medicine (D.O.)',
  'Board Certified — American Board of Integrative Medicine',
  'Board Certified — Anti-Aging and Regenerative Medicine',
  'Fellow, American Academy of Anti-Aging Medicine',
  '40+ years of clinical practice',
  'Specializations: Functional Medicine, Hormone Replacement Therapy, Longevity Medicine',
  'Chelation therapy, IV nutrient therapy, bioidentical hormones',
];

const howItWorks = [
  {
    step: '01',
    title: 'You Upload Your Labs',
    desc: 'Any recent bloodwork from Quest, Labcorp, or your primary care. The AI reads 40+ biomarkers instantly.',
  },
  {
    step: '02',
    title: 'The AI Builds Your Profile',
    desc: 'Every result is plotted across standard, functional, and optimal ranges. Patterns and connections are identified that a 90-second office visit would miss.',
  },
  {
    step: '03',
    title: 'Dr. Calapai Reviews Your Case',
    desc: 'Your full biomarker profile, medical history, and health goals are reviewed by Dr. Calapai personally. Not a nurse. Not a chatbot. A physician with 40 years of functional medicine experience.',
  },
  {
    step: '04',
    title: 'You Get a Personalized Protocol',
    desc: 'A clinical plan built around your specific numbers — targeted supplementation, hormone optimization, or lifestyle changes. Every recommendation is evidence-based and sourced.',
  },
];

// ─── Component ───

export function MeetTheDoctor() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: FONT_BODY, color: C.text }}>
      {/* ─── Scoped styles ─── */}
      <style>{`
        .doc-section { padding: clamp(48px, 8vw, 96px) clamp(20px, 5vw, 48px); }
        .doc-container { max-width: 960px; margin: 0 auto; }
        .doc-card {
          background: ${C.bgWhite};
          border: 1px solid ${C.border};
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }
        .doc-label {
          font-family: ${FONT_MONO};
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: ${C.accent};
          margin-bottom: 16px;
        }
        .doc-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px 40px;
          background: ${C.btn};
          color: #FFFFFF;
          border: none;
          border-radius: 4px;
          font-family: ${FONT_BODY};
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, box-shadow 0.2s;
        }
        .doc-btn-primary:hover {
          background: ${C.btnHover};
          box-shadow: 0 4px 16px rgba(24,24,27,0.12);
        }
        .doc-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          background: transparent;
          color: ${C.text};
          border: 1.5px solid ${C.border};
          border-radius: 4px;
          font-family: ${FONT_BODY};
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s;
        }
        .doc-btn-secondary:hover {
          border-color: ${C.accent};
          color: ${C.accent};
        }
        @media (max-width: 680px) {
          .doc-hero-grid { flex-direction: column !important; text-align: center; }
          .doc-hero-grid > div:first-child { align-items: center !important; }
          .doc-steps-grid { grid-template-columns: 1fr !important; }
          .doc-creds-grid { grid-template-columns: 1fr !important; }
          .doc-cta-btns { flex-direction: column; align-items: stretch; }
        }
      `}</style>

      {/* ─── Top bar ─── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: C.bgWhite, borderBottom: `1px solid ${C.border}`,
        padding: '12px clamp(20px, 5vw, 48px)',
      }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 20, color: C.textMuted, padding: '4px 8px',
                transition: 'color 0.15s',
              }}
              aria-label="Go back"
              onMouseEnter={(e) => (e.currentTarget.style.color = C.text)}
              onMouseLeave={(e) => (e.currentTarget.style.color = C.textMuted)}
            >
              &#8592;
            </button>
            <div>
              <div
                style={{ fontFamily: FONT_SERIF, fontSize: 18, color: C.text, lineHeight: 1 }}
              >
                Hemo<span style={{ color: C.accent }}>Logix</span>
              </div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>Meet Your Doctor</div>
            </div>
          </div>
          <button
            className="doc-btn-primary"
            style={{ padding: '10px 24px', fontSize: 13 }}
            onClick={() => navigate('/patient-chat')}
          >
            Start Your Analysis
          </button>
        </div>
      </div>

      {/* ─── HERO ─── */}
      <section className="doc-section" style={{ background: C.bgWhite, borderBottom: `1px solid ${C.border}` }}>
        <div className="doc-container">
          <div
            className="doc-hero-grid"
            style={{ display: 'flex', gap: 'clamp(32px, 5vw, 64px)', alignItems: 'center' }}
          >
            {/* Left: Doctor info */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <FadeIn>
                <div className="doc-label">MEDICAL DIRECTOR</div>
              </FadeIn>
              <FadeIn delay={0.05}>
                <h1 style={{
                  fontFamily: FONT_SERIF,
                  fontSize: 'clamp(32px, 5vw, 52px)',
                  fontWeight: 400,
                  lineHeight: 1.1,
                  color: C.navy,
                  marginBottom: 8,
                }}>
                  Dr. Christopher<br />Calapai<span style={{ color: C.accent }}>,</span> D.O.
                </h1>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p style={{
                  fontSize: 17,
                  lineHeight: 1.7,
                  color: C.textSec,
                  maxWidth: 460,
                  marginBottom: 28,
                }}>
                  Over four decades of clinical practice in functional medicine, hormone optimization,
                  and longevity. Not just reading your labs — understanding the story they tell.
                </p>
              </FadeIn>
              <FadeIn delay={0.15}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {['Functional Medicine', 'Hormone Therapy', 'Longevity', '40+ Years'].map((tag) => (
                    <span
                      key={tag}
                      style={{
                        padding: '6px 16px',
                        background: C.accentSoft,
                        border: `1px solid ${C.accentGlow}`,
                        borderRadius: 4,
                        fontSize: 13,
                        fontWeight: 500,
                        color: C.accent,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </FadeIn>
            </div>

            {/* Right: Doctor placeholder visual */}
            <FadeIn delay={0.1} direction="left">
              <div style={{
                width: 'clamp(200px, 28vw, 300px)',
                height: 'clamp(240px, 34vw, 360px)',
                borderRadius: 4,
                background: `linear-gradient(135deg, ${C.borderLight} 0%, ${C.border} 100%)`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: `1px solid ${C.border}`,
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Subtle accent stripe */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 4,
                  background: C.accent,
                }} />
                <div style={{
                  width: 72, height: 72, borderRadius: 4,
                  background: C.accentSoft, border: `2px solid ${C.accentGlow}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 16,
                }}>
                  <span style={{ fontSize: 28, color: C.accent }}>&#9877;</span>
                </div>
                <div style={{
                  fontFamily: FONT_SERIF,
                  fontSize: 16,
                  color: C.textSec,
                }}>
                  Dr. Calapai, D.O.
                </div>
                <div style={{
                  fontSize: 12,
                  color: C.textMuted,
                  marginTop: 4,
                }}>
                  CC Medical PC
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── BIO / CLINICAL PHILOSOPHY ─── */}
      <section className="doc-section" style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <div className="doc-container">
          <FadeIn>
            <div className="doc-label">CLINICAL PHILOSOPHY</div>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h2 style={{
              fontFamily: FONT_SERIF,
              fontSize: 'clamp(24px, 4vw, 38px)',
              fontWeight: 400,
              lineHeight: 1.2,
              color: C.navy,
              marginBottom: 24,
            }}>
              Your labs have a story.{' '}
              <span style={{ fontStyle: 'italic', color: C.accent }}>Most doctors don't have time to read it.</span>
            </h2>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            <FadeIn delay={0.1}>
              <div className="doc-card" style={{ padding: 'clamp(20px, 3vw, 32px)' }}>
                <div style={{ fontSize: 20, marginBottom: 12, color: C.accent }}>&#9670;</div>
                <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 10, lineHeight: 1.3 }}>
                  Functional Ranges, Not Just "Normal"
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.8, color: C.textSec }}>
                  Standard lab ranges tell you whether you're sick. Functional medicine asks a different question:
                  are you <em>optimal</em>? Dr. Calapai evaluates your biomarkers against the ranges where the body
                  actually performs at its best — not the ranges designed to catch disease after it's already arrived.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="doc-card" style={{ padding: 'clamp(20px, 3vw, 32px)' }}>
                <div style={{ fontSize: 20, marginBottom: 12, color: C.accent }}>&#9670;</div>
                <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 10, lineHeight: 1.3 }}>
                  The Whole Picture, Not Isolated Numbers
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.8, color: C.textSec }}>
                  Your thyroid markers affect your hormones. Your hormones affect your cortisol. Your cortisol
                  affects your blood sugar. Dr. Calapai reads labs the way they were meant to be read — as an
                  interconnected system, not a checklist of isolated values. Patterns tell the story that individual
                  numbers can't.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="doc-card" style={{ padding: 'clamp(20px, 3vw, 32px)' }}>
                <div style={{ fontSize: 20, marginBottom: 12, color: C.accent }}>&#9670;</div>
                <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 10, lineHeight: 1.3 }}>
                  40 Years of Seeing What Others Miss
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.8, color: C.textSec }}>
                  Four decades of functional medicine means Dr. Calapai has seen the patterns that textbooks
                  don't cover — the subclinical thyroid dysfunction masked by "normal" TSH, the early insulin
                  resistance hiding behind a clean fasting glucose, the hormone imbalances that explain years
                  of unexplained symptoms.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── MSO/PC MODEL (patient-friendly) ─── */}
      <section className="doc-section" style={{ background: C.bgWhite, borderBottom: `1px solid ${C.border}` }}>
        <div className="doc-container" style={{ maxWidth: 720 }}>
          <FadeIn>
            <div className="doc-label">YOUR CARE STRUCTURE</div>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h2 style={{
              fontFamily: FONT_SERIF,
              fontSize: 'clamp(24px, 4vw, 38px)',
              fontWeight: 400,
              lineHeight: 1.2,
              color: C.navy,
              marginBottom: 12,
            }}>
              Independent medical care,{' '}
              <span style={{ fontStyle: 'italic', color: C.accent }}>powered by technology.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p style={{ fontSize: 15, lineHeight: 1.8, color: C.textSec, marginBottom: 36 }}>
              Your clinical care is provided by CC Medical PC, Dr. Calapai's independent medical practice.
              HemoLogix provides the technology platform — the AI analysis, the biomarker tracking, the
              reporting tools. The medicine stays with the doctor. The technology stays with the platform.
            </p>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 20,
            }}>
              {/* Doctor side */}
              <div className="doc-card" style={{
                padding: 'clamp(20px, 3vw, 28px)',
                borderLeft: `3px solid ${C.accent}`,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: 2,
                  color: C.accent, textTransform: 'uppercase', marginBottom: 12,
                  fontFamily: FONT_MONO,
                }}>
                  CC Medical PC
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
                  Your Medical Practice
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Clinical decisions and prescribing',
                    'Lab result interpretation',
                    'Personalized treatment protocols',
                    'Physician-patient relationship',
                    'Medical record management',
                  ].map((item) => (
                    <li key={item} style={{
                      fontSize: 14, lineHeight: 1.7, color: C.textSec,
                      paddingLeft: 20, position: 'relative', marginBottom: 4,
                    }}>
                      <span style={{
                        position: 'absolute', left: 0, top: 3,
                        width: 8, height: 8, borderRadius: 2,
                        background: C.accentSoft, border: `1px solid ${C.accentGlow}`,
                      }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Platform side */}
              <div className="doc-card" style={{
                padding: 'clamp(20px, 3vw, 28px)',
                borderLeft: `3px solid ${C.border}`,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: 2,
                  color: C.textMuted, textTransform: 'uppercase', marginBottom: 12,
                  fontFamily: FONT_MONO,
                }}>
                  HemoLogix Platform
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
                  Your Technology
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'AI-powered biomarker analysis',
                    'Functional + optimal range mapping',
                    'Secure lab upload and storage',
                    'Progress tracking over time',
                    'HIPAA-grade data encryption',
                  ].map((item) => (
                    <li key={item} style={{
                      fontSize: 14, lineHeight: 1.7, color: C.textSec,
                      paddingLeft: 20, position: 'relative', marginBottom: 4,
                    }}>
                      <span style={{
                        position: 'absolute', left: 0, top: 3,
                        width: 8, height: 8, borderRadius: 2,
                        background: C.borderLight, border: `1px solid ${C.border}`,
                      }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p style={{
              fontSize: 13, lineHeight: 1.7, color: C.textMuted,
              marginTop: 24, textAlign: 'center',
              padding: '14px 20px',
              background: C.bg,
              borderRadius: 4,
              border: `1px solid ${C.borderLight}`,
            }}>
              This structure ensures your physician maintains full clinical independence.
              Technology supports the medicine — it never replaces it.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ─── CREDENTIALS ─── */}
      <section className="doc-section" style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <div className="doc-container">
          <FadeIn>
            <div className="doc-label">CREDENTIALS &amp; CERTIFICATIONS</div>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h2 style={{
              fontFamily: FONT_SERIF,
              fontSize: 'clamp(24px, 4vw, 38px)',
              fontWeight: 400,
              lineHeight: 1.2,
              color: C.navy,
              marginBottom: 36,
            }}>
              The experience behind{' '}
              <span style={{ fontStyle: 'italic', color: C.accent }}>your protocol.</span>
            </h2>
          </FadeIn>

          <div
            className="doc-creds-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}
          >
            {credentials.map((cred, i) => (
              <FadeIn key={i} delay={i * 0.04}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  padding: '16px 20px',
                  background: C.bgWhite,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 4, flexShrink: 0,
                    background: C.accentSoft, border: `1px solid ${C.accentGlow}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, color: C.accent, fontWeight: 700,
                    fontFamily: FONT_MONO,
                  }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <p style={{ fontSize: 14, lineHeight: 1.6, color: C.textSec, margin: 0 }}>{cred}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS WITH HEMOLOGIX ─── */}
      <section className="doc-section" style={{ background: C.bgWhite, borderBottom: `1px solid ${C.border}` }}>
        <div className="doc-container">
          <FadeIn>
            <div className="doc-label">HOW IT WORKS</div>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h2 style={{
              fontFamily: FONT_SERIF,
              fontSize: 'clamp(24px, 4vw, 38px)',
              fontWeight: 400,
              lineHeight: 1.2,
              color: C.navy,
              marginBottom: 14,
            }}>
              AI precision.{' '}
              <span style={{ fontStyle: 'italic', color: C.accent }}>Physician judgment.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p style={{ fontSize: 15, lineHeight: 1.8, color: C.textSec, maxWidth: 620, marginBottom: 48 }}>
              The AI analyzes your labs in seconds. Dr. Calapai reviews every case personally
              and builds your protocol with four decades of clinical insight.
            </p>
          </FadeIn>

          <div
            className="doc-steps-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}
          >
            {howItWorks.map((step, i) => (
              <FadeIn key={i} delay={0.1 + i * 0.06}>
                <div style={{ position: 'relative' }}>
                  {/* Step number */}
                  <div style={{
                    fontFamily: FONT_MONO,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    color: C.accent,
                    marginBottom: 10,
                  }}>
                    STEP {step.step}
                  </div>

                  {/* Connector line */}
                  {i < howItWorks.length - 1 && (
                    <div style={{
                      position: 'absolute',
                      top: 8,
                      right: -12,
                      width: 24,
                      height: 1,
                      background: C.border,
                    }} className="doc-connector" />
                  )}

                  <h3 style={{
                    fontSize: 17,
                    fontWeight: 600,
                    marginBottom: 10,
                    lineHeight: 1.3,
                    color: C.text,
                  }}>
                    {step.title}
                  </h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: C.textSec }}>
                    {step.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Emphasis box */}
          <FadeIn delay={0.4}>
            <div style={{
              marginTop: 48,
              padding: 'clamp(20px, 3vw, 32px)',
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderLeft: `3px solid ${C.accent}`,
              borderRadius: 4,
            }}>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: C.textSec, margin: 0 }}>
                <strong style={{ color: C.text }}>The Captain and the Co-Pilot.</strong>{' '}
                Dr. Calapai is the clinical authority behind every protocol — the Captain.
                The AI handles the heavy analysis: reading 40+ biomarkers across multiple range systems,
                identifying patterns across interconnected markers, surfacing the connections that matter.
                The doctor makes the decisions. The technology makes sure nothing gets missed.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="doc-section" style={{
        background: C.bg,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Subtle background pattern */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.2,
          backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
          backgroundSize: '72px 72px',
          maskImage: 'radial-gradient(ellipse 55% 50% at 50% 50%, black 20%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 55% 50% at 50% 50%, black 20%, transparent 75%)',
          pointerEvents: 'none',
        }} />
        {/* Accent glow */}
        <div style={{
          position: 'absolute', width: '50%', height: '60%', top: '20%', left: '25%',
          background: `radial-gradient(ellipse, ${C.accentSoft} 0%, transparent 70%)`,
          pointerEvents: 'none', opacity: 0.5,
        }} />

        <div className="doc-container" style={{ position: 'relative' }}>
          <FadeIn>
            <h2 style={{
              fontFamily: FONT_SERIF,
              fontSize: 'clamp(28px, 5vw, 52px)',
              fontWeight: 400,
              lineHeight: 1.1,
              color: C.navy,
              marginBottom: 16,
            }}>
              Ready to see what your labs{' '}
              <span style={{
                background: `linear-gradient(135deg, ${C.accent}, #FB7185)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                really say?
              </span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p style={{
              fontSize: 16, lineHeight: 1.7, color: C.textSec,
              maxWidth: 480, margin: '0 auto 36px',
            }}>
              Upload your bloodwork for a free AI analysis.
              When you're ready, Dr. Calapai builds your personalized protocol.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="doc-cta-btns" style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="doc-btn-primary"
                style={{ padding: '18px 44px', fontSize: 16 }}
                onClick={() => navigate('/patient-chat')}
              >
                Start Your Free Analysis
              </button>
              <button
                className="doc-btn-secondary"
                style={{ padding: '18px 32px', fontSize: 15 }}
                onClick={() => navigate('/landing')}
              >
                Upload Your Labs
              </button>
            </div>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p style={{
              fontSize: 12, color: C.textMuted, marginTop: 18,
            }}>
              No account required &middot; Results in under 2 minutes &middot; HIPAA-grade encryption
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{
        padding: '36px clamp(20px, 5vw, 48px) 24px',
        borderTop: `1px solid ${C.border}`,
        background: C.bgSoft,
        textAlign: 'center',
      }}>
        <p style={{ fontSize: 12, lineHeight: 1.7, color: C.textMuted, maxWidth: 680, margin: '0 auto 12px' }}>
          Hemologix is an educational comparison tool that plots lab values against published reference ranges.
          It does not provide medical diagnoses, treatment recommendations, or clinical advice.
          All clinical decisions are made by independently licensed physicians through CC Medical PC.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
          <span
            style={{ fontSize: 13, color: C.textMuted, cursor: 'pointer', transition: 'color 0.15s' }}
            onClick={() => navigate('/privacy')}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.text)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.textMuted)}
          >
            Privacy Policy
          </span>
          <span
            style={{ fontSize: 13, color: C.textMuted, cursor: 'pointer', transition: 'color 0.15s' }}
            onClick={() => navigate('/terms')}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.text)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.textMuted)}
          >
            Terms of Service
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'rgba(161,161,170,0.6)', marginTop: 16 }}>
          &copy; 2026 Hemologix Health, Inc. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
